import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import * as moment from 'moment-timezone';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerService } from '../../../../../../../../../../services/customer/customer.service';
import { dateStringFromISO8601, getISO8601DateStringFromMoment } from '../../../../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../../../../services/organization-config/organization-config.service';
import { CustomerMembership, isOutsideCancellationDateRange } from '../../../../../../../../../../models/membership';
import {
  getMembershipBillingIntervalStart,
  getProratedRemainingAmount
} from '../../../../../../../../../../utils-models';
import { MembershipInterval } from '../../../../../../../../../../models/membership-enums';
import { PaymentType } from '../../../../../../../../../../models/payment-enums';
import {
  getRefundBlockedByPaymentTypeMessage
} from '../../../../../../../../../../models/payment';


enum REFUND_POLICY {
  noRefund,
  refundLastPayment,
  refundCredit,
}


@Component({
  selector: 'app-edit-recurring-customer-membership-cancel-immediately',
  templateUrl: './edit-recurring-customer-membership-cancel-immediately.component.html',
  styleUrls: ['./edit-recurring-customer-membership-cancel-immediately.component.css']
})
export class EditRecurringCustomerMembershipCancelImmediatelyComponent implements OnInit, OnDestroy {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;
  @Input() saveClickedObservable: Observable<void>;
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() serverErrorEvent = new EventEmitter<number>();

  public refundPolicy: REFUND_POLICY = REFUND_POLICY.noRefund;
  public refundCreditAmount = 0;
  public cancellationEmailText = '';
  public reason: string = null;

  // Set when the last invoice was paid with a tender Capitan cannot refund (a
  // POS or offline payment), which disables the refund option.
  public refundBlockedPaymentType: PaymentType | null = null;

  // Staff cancellations are not restricted; this only warns staff that a
  // customer could not cancel today.
  public todayOutsideCancellationDateRange = false;

  public errorMessage = '';

  public dateStringFromISO8601 = dateStringFromISO8601;
  public REFUND_POLICY = REFUND_POLICY;

  private saveClickedSubscription: Subscription;

  constructor(
    public orgConfigService: OrganizationConfigService,
    private activeModal: NgbActiveModal,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    this.saveClickedSubscription = this.saveClickedObservable.subscribe(() => {
      this.handleSave();
    });

    if (!this.membership.last_invoice_amount) {
      if (this.refundPolicy === REFUND_POLICY.refundLastPayment) {
        this.refundPolicy = REFUND_POLICY.noRefund;
      }
    }

    this.refundBlockedPaymentType =
      this.membership.last_invoice_refund_blocking_payment_type;

    // Belt and braces alongside the disabled radio: the server rejects a refund
    // of this tender outright, which abandons the whole cancellation.
    if (this.refundBlockedPaymentType
        && (this.refundPolicy === REFUND_POLICY.refundLastPayment)) {
      this.refundPolicy = REFUND_POLICY.noRefund;
    }

    this.todayOutsideCancellationDateRange = isOutsideCancellationDateRange(
      this.membership,
      getISO8601DateStringFromMoment(moment())
    );

    // Reload the org config, in case the membership cancellation reason policy has changed.
    this.orgConfigService.loadOrganizationConfig();

    const todayStart = moment().startOf('day');

    const billingPeriodEnd = moment(this.membership.end_date);
    let billingPeriodStart = getMembershipBillingIntervalStart(
      billingPeriodEnd,
      this.membership.interval as MembershipInterval
    );

    // Handle memberships with shorter first billing periods due to fixed billing dates.
    const membershipStartMoment = moment(this.membership.start_date);
    if (membershipStartMoment > billingPeriodStart) {
      billingPeriodStart = membershipStartMoment;
    }

    this.refundCreditAmount = getProratedRemainingAmount(
      parseFloat(this.membership.billing_amount),
      billingPeriodStart,
      billingPeriodEnd,
      todayStart
    );
  }

  ngOnDestroy() {
    this.saveClickedSubscription.unsubscribe();
  }

  /**
   * The warning shown when Capitan cannot refund this membership's last
   * payment, explaining where the refund has to be issued instead. Empty when
   * the payment is refundable from here.
   */
  get refundBlockedMessage(): string {
    if (!this.refundBlockedPaymentType) {
      return '';
    }

    let message = getRefundBlockedByPaymentTypeMessage(
      this.refundBlockedPaymentType
    );

    if (this.refundCreditAmount > 0) {
      message += ' You can still issue prorated account credit below.';
    }

    return message;
  }

  private handleSave() {
    const orgConfig = this.orgConfigService.getOrganizationConfig();
    if (orgConfig.require_membership_cancellation_reason && (this.reason === null)) {
      this.errorMessage = 'Please select a reason for the cancellation.';
      return;
    }

    this.loadingEvent.emit(true);

    this.errorMessage = '';

    this.customerService
      .cancelMembershipImmediately(
        this.membership,
        this.cancellationEmailText,
        this.refundPolicy === REFUND_POLICY.refundLastPayment,
        (this.refundPolicy === REFUND_POLICY.refundCredit ? this.refundCreditAmount : 0),
        this.reason
      )
      .then(() => {
        this.savedCallback('Membership cancelled successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to cancel this membership.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => { this.loadingEvent.emit(false); });
  }

}
